import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

interface TagsResponse {
  tags: string[]
}

export function useChatTags(chatId: string | null, channel: string | null) {
  const [tags, setTags] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const url = chatId && channel ? `/agent/chats/${chatId}/tags?channel=${encodeURIComponent(channel)}` : null

  useEffect(() => {
    if (!url) return
    setLoading(true)
    void api
      .get<TagsResponse>(url)
      .then((res) => setTags(res.tags ?? []))
      .catch(() => setTags([]))
      .finally(() => setLoading(false))
  }, [url])

  const save = useCallback(
    async (next: string[]) => {
      if (!url) return
      setLoading(true)
      try {
        const res = await api.post<TagsResponse>(url, { tags: next })
        setTags(res.tags ?? next)
      } catch {
        // keep the old tags on error — the caller can try again
      } finally {
        setLoading(false)
      }
    },
    [url],
  )

  const addTag = useCallback(
    async (raw: string) => {
      const tag = raw.trim()
      if (!tag || tags.includes(tag)) return
      await save([...tags, tag])
    },
    [tags, save],
  )

  const removeTag = useCallback(
    async (tag: string) => {
      await save(tags.filter((t) => t !== tag))
    },
    [tags, save],
  )

  return { tags, loading, addTag, removeTag }
}
